import { api } from '../api';

// Unwrap the ApiResponse<PageResponse<T>> envelope down to a plain array so
// pages can iterate without knowing the wire shape.
const toArray = (body) =>
  body?.data?.content   // ApiResponse<PageResponse<T>>
  ?? body?.content      // raw PageResponse<T>
  ?? (Array.isArray(body?.data) ? body.data : null)
  ?? (Array.isArray(body) ? body : []);

// Unwrap the ApiResponse<T> envelope for non-paged responses (single objects,
// plain maps, etc.). Returns `data` if the wire shape was the envelope,
// otherwise the body as-is.
const toData = (body) => body?.data ?? body ?? null;

export default {
  // ---- pool & manual lifecycle (Engine v2 Phase 1) --------------------------
  // Mark a Lead (CRM) or Customer as lost. Pass exactly one of
  // { leadId } / { customerId } plus an optional reason.
  mark:           (body) => api.post('/v1/dead-leads/mark', body).then(r => toData(r.data)),
  listPool:       (params) => api.get('/v1/dead-leads/pool', { params }).then(r => toArray(r.data)),
  getPoolRow:     (id) => api.get(`/v1/dead-leads/pool/${id}`).then(r => toData(r.data)),
  updatePoolStatus: (id, body) => api.patch(`/v1/dead-leads/pool/${id}`, body).then(r => toData(r.data)),
  moveToLead:     (id) => api.post(`/v1/dead-leads/${id}/move-to-lead`).then(r => toData(r.data)),
  moveToCustomer: (id) => api.post(`/v1/dead-leads/${id}/move-to-customer`).then(r => toData(r.data)),
  listPersonas:   () => api.get('/v1/dead-leads/personas').then(r => {
    const body = r.data;
    return Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
  }),
  createPersona:  (body) => api.post('/v1/dead-leads/personas', body).then(r => toData(r.data)),
  updatePersona:  (id, body) => api.patch(`/v1/dead-leads/personas/${id}`, body).then(r => toData(r.data)),

  // ---- qualification, persona inference, batching (Engine v2 Phase 2-3) -----
  qualify:        (id) => api.post(`/v1/dead-leads/${id}/qualify`).then(r => toData(r.data)),
  inferPersona:   (id) => api.post(`/v1/dead-leads/${id}/infer-persona`).then(r => toData(r.data)),
  buildBatches:   (body) => api.post('/v1/dead-leads/batches/build', body || {}).then(r => toData(r.data)),
  getSettings:    () => api.get('/v1/dead-leads/settings').then(r => toData(r.data) || {}),

  // ---- targets --------------------------------------------------------------
  listTargets:    (params) => api.get('/v1/dead-leads/targets', { params }).then(r => toArray(r.data)),
  targetConversation: (id) =>
    api.get(`/v1/dead-leads/targets/${id}/conversation`).then(r => {
      const body = r.data;
      return Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    }),
  // ---- take-over (Engine v2 Phase 5) ----------------------------------------
  replyTarget: (id, body) => api.post(`/v1/dead-leads/targets/${id}/reply`, { body }).then(r => toData(r.data)),
  pauseAi:     (id) => api.post(`/v1/dead-leads/targets/${id}/pause-ai`).then(r => toData(r.data)),
  resumeAi:    (id) => api.post(`/v1/dead-leads/targets/${id}/resume-ai`).then(r => toData(r.data)),
  updateTarget:   (id, body) => api.patch(`/v1/dead-leads/targets/${id}`, body).then(r => toData(r.data)),
  promote:        (id, body) => api.post(`/v1/dead-leads/targets/${id}/promote`, body || {}).then(r => toData(r.data)),
  targetsForCustomer: (customerId) =>
    api.get(`/v1/dead-leads/customers/${customerId}/targets`).then(r => {
      const body = r.data;
      // findByCustomerIdOrderByIdDesc returns a plain List, wrapped in ApiResponse
      return Array.isArray(body?.data) ? body.data
           : Array.isArray(body) ? body
           : [];
    }),

  // ---- campaigns ------------------------------------------------------------
  listCampaigns:  (params) =>
    api.get('/v1/dead-leads/campaigns', { params }).then(r => {
      const body = r.data;
      // Controller returns plain List<DeadLeadCampaign> inside ApiResponse.
      return Array.isArray(body?.data) ? body.data
           : Array.isArray(body) ? body
           : [];
    }),
  getCampaign:    (id) => api.get(`/v1/dead-leads/campaigns/${id}`).then(r => toData(r.data)),
  createCampaign: (body) => api.post('/v1/dead-leads/campaigns', body).then(r => toData(r.data)),
  updateCampaign: (id, body) => api.patch(`/v1/dead-leads/campaigns/${id}`, body).then(r => toData(r.data)),
  launchCampaign: (id) => api.post(`/v1/dead-leads/campaigns/${id}/launch`).then(r => toData(r.data)),
  pauseCampaign:  (id) => api.post(`/v1/dead-leads/campaigns/${id}/pause`).then(r => toData(r.data)),
  resumeCampaign: (id) => api.post(`/v1/dead-leads/campaigns/${id}/resume`).then(r => toData(r.data)),
  cancelCampaign: (id) => api.post(`/v1/dead-leads/campaigns/${id}/cancel`).then(r => toData(r.data)),
  deleteCampaign: (id) => api.delete(`/v1/dead-leads/campaigns/${id}`).then(r => toData(r.data)),

  // ---- funnel ---------------------------------------------------------------
  campaignFunnel: (id) =>
    api.get(`/v1/dead-leads/campaigns/${id}/funnel`).then(r => toData(r.data) || {}),
  overallFunnel:  (params) =>
    api.get('/v1/dead-leads/funnel', { params }).then(r => toData(r.data) || {}),
};
